import React from 'react';
import { Button, Form, Overlay, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './supplement-picker.css';
import { CatalogSupplement, Section, SupplementEdit } from '../../models';
import { normalizeSupplementName } from '../../models/holobiomeKits';
import { SECTION_HEADINGS } from './supplements-summary-panel';

/**
 * "Add a supplement" — the whole supplement catalog, offered by report section.
 *
 * The card it opens from can only ever hold what a pattern pointed to, and a
 * practitioner routinely needs a product blood work cannot reach: Digestion is
 * authored but unplumbed, two of the Neuro formulas have no marker behind them
 * at all, and a client already taking something should keep taking it.
 *
 * Shaped like MarkerPicker on Add Test Results (fold by section, filter, stays
 * open after a pick), because the same thing is true here: it is a long list
 * used several times in one sitting, and a dropdown that closes on every pick
 * turns a five-product protocol into five trips.
 *
 * Two deliberate differences from that picker:
 *
 *  - NO "Add all N". It is right for a fourteen-marker mold panel and wrong for
 *    a section holding fifty-seven digestion products.
 *  - The filter is ALWAYS shown, not only past six items. The catalog is far
 *    past the point where anyone scrolls it looking for a name.
 *
 * With NO catalog — every brand unchecked in Supplement Settings — it collapses
 * to the manual form alone (`manualOnly`). Nothing can be picked, so the list,
 * the filter and the "not on the list?" link are all furniture around an empty
 * box; what is left is the practitioner typing their own protocol, which is what
 * the Recommended Supplements card goes on being for once no brand is on.
 */

// A section heading for the picker, and the order sections are offered in.
const SECTION_LABEL = new Map(
  SECTION_HEADINGS.map(({ section, label }) => [String(section), label] as const),
);

// Products the sheet has not filed under any section yet. Named rather than
// hidden: a catalog row with no sections column is still a product the
// practitioner may want, and silently dropping it would look like a missing
// product rather than missing data.
export const UNFILED = 'Other products';

export type PickerGroup = {
  key: string;
  label: string;
  products: CatalogSupplement[];
};

/**
 * Group the catalog by report section, worst-affected section first.
 *
 * `severityOrder` is the Results Overview ranking, the same one the by-system
 * tables and the client list already use, so the picker opens on the system the
 * practitioner is looking at the report about. Sections the ranking cannot see
 * (untested, no valued markers) follow in canonical report order, and unfiled
 * products come last.
 *
 * A product filed under three sections is offered under all three. It is one
 * bottle either way — `inUse` and the card's own dedupe make sure picking it
 * twice cannot produce two rows.
 */
export function groupCatalogBySection(
  catalog: CatalogSupplement[],
  severityOrder: Section[],
): PickerGroup[] {
  const order: string[] = [];
  const push = (section: string) => {
    if (section && order.indexOf(section) === -1) order.push(section);
  };
  (severityOrder || []).forEach(section => push(String(section)));
  SECTION_HEADINGS.forEach(({ section }) => push(String(section)));

  const bySection = new Map<string, CatalogSupplement[]>();
  (catalog || []).forEach(product => {
    const sections = product.sections && product.sections.length
      ? product.sections
      : [UNFILED];
    sections.forEach(section => {
      const key = SECTION_LABEL.has(section) || section === UNFILED ? section : UNFILED;
      if (!bySection.has(key)) bySection.set(key, []);
      bySection.get(key)!.push(product);
    });
  });

  const groups: PickerGroup[] = [];
  order.forEach(section => {
    const products = bySection.get(section);
    if (products && products.length) {
      groups.push({
        key: section,
        label: SECTION_LABEL.get(section) || section,
        products: products.slice().sort(byName),
      });
    }
  });
  const unfiled = bySection.get(UNFILED);
  if (unfiled && unfiled.length) {
    groups.push({ key: UNFILED, label: UNFILED, products: unfiled.slice().sort(byName) });
  }
  return groups;
}

const byName = (a: CatalogSupplement, b: CatalogSupplement): number =>
  a.name.localeCompare(b.name);

const INPUT_STYLE: React.CSSProperties = {
  fontSize: '12px',
  height: '30px',
  padding: '2px 8px',
};

/**
 * The "not on the list?" form. Everything the client list prints, for a product
 * LabSmarts has never heard of — another brand, a practice's own formulation.
 *
 * The link is optional and never guessed: systemicFormulasProductUrl only knows
 * one brand's URLs, so inventing one here would point a client at a page that
 * does not sell what they were told to buy. No link renders the name unlinked.
 */
const ManualEntry: React.FC<{
  onAdd: (edit: SupplementEdit) => void;
  /** Absent when manual entry is the whole picker — there is nothing to go back to. */
  onCancel?: () => void;
}> = ({ onAdd, onCancel }) => {
  const [name, setName] = React.useState('');
  const [link, setLink] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [dose, setDose] = React.useState('');
  const [therapeuticDose, setTherapeuticDose] = React.useState('');

  const trimmedName = name.trim();
  const trimmedLink = link.trim();
  const linkIsBad = !!trimmedLink && !/^https?:\/\//i.test(trimmedLink);

  const submit = () => {
    if (!trimmedName || linkIsBad) return;
    onAdd(
      new SupplementEdit({
        name: trimmedName,
        origin: 'added',
        link: trimmedLink || undefined,
        description: description.trim() || undefined,
        dose: dose.trim() || undefined,
        therapeuticDose: therapeuticDose.trim() || undefined,
      }),
    );
    setName('');
    setLink('');
    setDescription('');
    setDose('');
    setTherapeuticDose('');
  };

  return (
    <div className="supplement-picker-manual">
      <Form.Control
        type="text"
        style={INPUT_STYLE}
        placeholder="Product name"
        value={name}
        onChange={(e: any) => setName(e.target.value)}
      />
      <Form.Control
        type="text"
        style={INPUT_STYLE}
        placeholder="Product link (optional)"
        value={link}
        onChange={(e: any) => setLink(e.target.value)}
      />
      {linkIsBad && (
        <div className="supplement-picker-hint is-problem">
          A link has to start with http:// or https://
        </div>
      )}
      <Form.Control
        as="textarea"
        rows={2}
        style={{ ...INPUT_STYLE, height: 'auto' }}
        placeholder="What it is for (printed for the client)"
        value={description}
        onChange={(e: any) => setDescription(e.target.value)}
      />
      <Form.Control
        type="text"
        style={INPUT_STYLE}
        placeholder="Maintenance dose"
        value={dose}
        onChange={(e: any) => setDose(e.target.value)}
      />
      <Form.Control
        type="text"
        style={INPUT_STYLE}
        placeholder="Therapeutic dose (optional)"
        value={therapeuticDose}
        onChange={(e: any) => setTherapeuticDose(e.target.value)}
      />
      <div className="supplement-picker-manual-actions">
        {onCancel && (
          <Button
            variant="outline-secondary"
            size="sm"
            style={{ fontSize: '11px' }}
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          style={{ fontSize: '11px' }}
          disabled={!trimmedName || linkIsBad}
          onClick={submit}
        >
          Add to list
        </Button>
      </div>
    </div>
  );
};

type Props = {
  catalog: CatalogSupplement[];
  /** The Results Overview ranking, worst section first. */
  severityOrder: Section[];
  /** Normalized names already on the client's list, so they read as added. */
  inUse: Set<string>;
  onAdd: (edit: SupplementEdit) => void;
};

export const SupplementPicker: React.FC<Props> = ({
  catalog,
  severityOrder,
  inUse,
  onAdd,
}) => {
  const [filter, setFilter] = React.useState('');
  const [open, setOpen] = React.useState<{ [key: string]: boolean }>({});
  const [manual, setManual] = React.useState(false);
  // CONTROLLED, rather than the OverlayTrigger this used to be, so the popover
  // has a way to close itself. It stays open across picks on purpose (a
  // five-product protocol is one trip, not five), which left "click somewhere
  // else" as the only way out — not something the box says anywhere, and a
  // click that lands on another control is spent closing this one instead of
  // pressing that. Done says it, and costs one click.
  const [show, setShow] = React.useState(false);
  // The ref sits on a wrapper rather than on the Button: react-bootstrap types
  // a Button ref as the class instance, which is not what Overlay's `target`
  // wants. The span is inline-block so it measures as the button does.
  const trigger = React.useRef<HTMLSpanElement | null>(null);

  const text = filter.trim().toLowerCase();
  const matching = text
    ? catalog.filter(product =>
        `${product.name} ${product.description || ''}`.toLowerCase().includes(text),
      )
    : catalog;
  const groups = groupCatalogBySection(matching, severityOrder);
  const onlyUnfiled = groups.length === 1 && groups[0].key === UNFILED;
  // NO CATALOG AT ALL — every brand unchecked in Supplement Settings. There is
  // nothing to filter and nothing to fold, so the picker is the manual form and
  // only the manual form: a filter box over an empty list, and a link asking to
  // open the one thing that can be done, are both furniture around a dead end.
  const manualOnly = catalog.length === 0;

  return (
    <>
      <span ref={trigger} style={{ display: 'inline-block' }}>
        <Button
          variant="outline-primary"
          size="sm"
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setShow(!show)}
        >
          + Add Supplement
        </Button>
      </span>
      <Overlay
        target={trigger.current}
        show={show}
        rootClose
        onHide={() => setShow(false)}
        placement="bottom-start"
      >
        <Popover id="supplement-picker" className="supplement-picker-popover">
          <Popover.Title
            as="h6"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
            }}
          >
            <span>Add a supplement</span>
            <Button
              variant="outline-secondary"
              size="sm"
              style={{ fontSize: '11px', padding: '1px 10px' }}
              onClick={() => setShow(false)}
            >
              Done
            </Button>
          </Popover.Title>
          <Popover.Content>
            {manualOnly && (
              <div className="supplement-picker-hint" style={{ marginBottom: '6px' }}>
                No supplement brand is switched on, so there is no catalog to
                pick from. Type in whatever you recommend.
              </div>
            )}
            {!manualOnly && (
              <Form.Control
                type="text"
                style={{ ...INPUT_STYLE, marginBottom: '6px' }}
                placeholder="Filter products"
                value={filter}
                onChange={(e: any) => setFilter(e.target.value)}
              />
            )}
            {!manualOnly && (
            <div className="supplement-picker">
              {groups.length === 0 && (
                <div className="supplement-picker-hint">
                  No products match that filter.
                </div>
              )}
              {groups.map(group => {
                // Open when filtering: a filter is a search for a product, and
                // folding the answer away is the dropdown's problem again.
                //
                // Also open when there is nothing to fold: until the catalog
                // carries its sections column, every product is unfiled, and a
                // single "Other products" heading over the whole list is a lid
                // on it rather than a way through it.
                const expanded = text || onlyUnfiled ? true : !!open[group.key];
                return (
                  <div key={group.key} className="supplement-picker-section">
                    {!onlyUnfiled && (
                    <Button
                      variant="link"
                      size="sm"
                      className="supplement-picker-toggle"
                      onClick={() => setOpen({ ...open, [group.key]: !expanded })}
                    >
                      <FontAwesomeIcon
                        icon={expanded ? 'chevron-down' : 'chevron-right'}
                        style={{ fontSize: '10px' }}
                      />
                      &nbsp;&nbsp;
                      <span className="supplement-picker-name">{group.label}</span>
                      <span className="supplement-picker-count">
                        &nbsp;&nbsp;{group.products.length}
                      </span>
                    </Button>
                    )}
                    {expanded &&
                      group.products.map(product => {
                        const added = inUse.has(normalizeSupplementName(product.name));
                        return (
                          <div
                            key={`${group.key}-${product.tag || product.name}`}
                            className={
                              added
                                ? 'supplement-picker-product is-in-use'
                                : 'supplement-picker-product'
                            }
                            // The catalog description, for a practitioner who
                            // knows the product by what it does rather than by
                            // its Systemic Formulas number.
                            title={product.description || undefined}
                          >
                            {added ? (
                              <span>
                                <FontAwesomeIcon icon="check" />
                                &nbsp;{product.name}
                              </span>
                            ) : (
                              <Button
                                variant="link"
                                size="sm"
                                className="supplement-picker-add"
                                onClick={() =>
                                  onAdd(
                                    new SupplementEdit({
                                      name: product.name,
                                      origin: 'added',
                                      tag: product.tag || undefined,
                                    }),
                                  )
                                }
                              >
                                + {product.name}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
            )}
            {manual || manualOnly ? (
              <ManualEntry
                onAdd={edit => {
                  onAdd(edit);
                  setManual(false);
                }}
                onCancel={manualOnly ? undefined : () => setManual(false)}
              />
            ) : (
              <Button
                variant="link"
                size="sm"
                className="supplement-picker-manual-open"
                onClick={() => setManual(true)}
              >
                Not on the list? Add one manually
              </Button>
            )}
          </Popover.Content>
        </Popover>
      </Overlay>
    </>
  );
};
